// components/Modals/PayoutRequestModal.jsx

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Elements/Modal/Modal";
import MainButton from "../../components/Elements/MainButton/MainButton";
import SecondButton from "../../components/Elements/SecondButton/SecondButton";
import MainInput from "../../components/Elements/MainInput/MainInput";
import ErrorMessage from "../../components/Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../../components/Elements/SuccessMessage/SuccessMessage";
import AffiliationService from "../../services/AffiliationService";
import "../Profile/ProfileModals.css";

const PayoutRequestModal = ({ isOpen, onClose, walletId, maxAmount, onSuccess }) => {
  const { t } = useTranslation();
  // Form fields
  const [amount, setAmount] = useState("");
  const [bankAccountNo, setBankAccountNo] = useState("");

  // UI state
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sending, setSending] = useState(false);

  const resetState = () => {
    setAmount("");
    setBankAccountNo("");
    setError("");
    setSuccess("");
    setSending(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setError(t("errors.enterValidPayoutAmount", "Enter a valid payout amount"));
      return;
    }

    if (!bankAccountNo.trim()) {
      setError(t("errors.bankAccountRequired", "Bank account number is required"));
      return;
    }

    try {
      setSending(true);
      await AffiliationService.requestWalletPayout(walletId, parseFloat(amount), bankAccountNo.trim());
      setSuccess("Payout requested successfully.");
      onSuccess?.();
    } catch (err) {
      const backendError =
        err.response?.data?.detail || err.response?.data?.error || err.message || "Payout request failed";

      setError(backendError);
    } finally {
      setSending(false);
    }
  };

  const isButtonDisabled = sending || !amount || parseFloat(amount) <= 0 || !bankAccountNo.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={sending ? () => {} : onClose}
      title={t("affiliation.requestPayout")}
      actions={
        <>
          <SecondButton onClick={onClose} disabled={sending}>
            {t("common.cancel")}
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={isButtonDisabled}>
            {sending ? t("profile.requesting") : t("affiliation.requestPayout")}
          </MainButton>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="profile-modal-form">
        {/* Amount */}
        <label>
          {t("profile.amount")}
          <MainInput
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError("");
            }}
            disabled={sending}
            placeholder={maxAmount ? maxAmount.toFixed(2) : "0.00"}
          />
        </label>

        {/* Bank account number */}
        <label>
          {t("affiliation.bankAccountNo")}
          <MainInput
            type="text"
            value={bankAccountNo}
            onChange={(e) => {
              setBankAccountNo(e.target.value);
              setError("");
            }}
            disabled={sending}
            placeholder={t("affiliation.enterBankAccount")}
          />
        </label>

        {/* Error / success messages */}
        {error && (
          <div className="profile-transfer-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}
        {success && (
          <div className="profile-transfer-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default PayoutRequestModal;
